import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { Title, useTranslate } from 'react-admin';
import { Link } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';

/**
 * Simple, resilient Dashboard
 * 
 * Principles:
 * - KISS: No heavy data fetching on mount
 * - DRY: Reusable ResourceCard component
 * - SOLID: Single responsibility - just show quick links
 * - Resilient: No dependencies on backend availability
 */

interface ResourceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  color: string;
  badge?: string;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  title,
  description,
  icon,
  link,
  color,
  badge,
}) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      },
    }}
  >
    <CardHeader
      avatar={
        <Box
          sx={{
            bgcolor: color,
            borderRadius: '50%',
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {icon}
        </Box>
      }
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">{title}</Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              color="primary"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
      }
    />
    <CardContent sx={{ flexGrow: 1 }}>
      <Typography variant="body2" color="text.secondary" paragraph>
        {description}
      </Typography>
      <Button
        component={Link}
        to={link}
        variant="contained"
        fullWidth
        sx={{ mt: 'auto' }}
      >
        Открыть
      </Button>
    </CardContent>
  </Card>
);

export const Dashboard = () => {
  const translate = useTranslate();

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Dashboard" />

      {/* Welcome Section */}
      <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            👋 Добро пожаловать в Admin Panel
          </Typography>
          <Typography variant="body1">
            Мультисервисная панель управления для INITE Auth Service и Break3 Backend
          </Typography>
        </CardContent>
      </Card>

      {/* Auth Service Resources */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Auth Service
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 3,
          }}
        >
          <ResourceCard
            title="Пользователи Auth"
            description="Управление пользователями централизованного сервиса авторизации"
            icon={<PeopleIcon />}
            link="/auth-users"
            color="#1976d2"
            badge="Auth"
          />
          <ResourceCard
            title="OAuth Клиенты"
            description="Управление OAuth2 клиентами и их настройками"
            icon={<VpnKeyIcon />}
            link="/oauth-clients"
            color="#2e7d32"
            badge="Auth"
          />
        </Box>
      </Box>

      {/* Break3 Backend Resources */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          <FitnessCenterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Break3 Backend
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 3,
          }}
        >
          <ResourceCard
            title="Пользователи Break3"
            description="Пользователи приложения Break3"
            icon={<PeopleIcon />}
            link="/users"
            color="#0288d1"
          />
          <ResourceCard
            title="Челленджи"
            description="Управление челленджами и их событиями"
            icon={<FitnessCenterIcon />}
            link="/challenges"
            color="#00796b"
          />
          <ResourceCard
            title="Добавки"
            description="Каталог спортивных добавок"
            icon={<LocalPharmacyIcon />}
            link="/supplements"
            color="#f57c00"
          />
          <ResourceCard
            title="Заказы"
            description="Заказы добавок и их статусы"
            icon={<ShoppingCartIcon />}
            link="/supplementOrders"
            color="#7b1fa2"
          />
          <ResourceCard
            title="Уведомления"
            description="Система уведомлений и очередь отправки"
            icon={<NotificationsIcon />}
            link="/notifications"
            color="#c62828"
          />
        </Box>
      </Box>

      {/* Info Card */}
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            💡 <strong>Resilience:</strong> Админка работает независимо от доступности отдельных сервисов.
            Если один из бэкендов недоступен, остальные продолжают функционировать.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
